// ============================================================================
// Staff invitation flow — admin-issued onboarding for the five non-doctor
// roles: pharmacy_staff, lab_staff, radiology_staff, ambulance_staff,
// org_admin.
//
// Mirrors `api/invitations.js` (which handles doctor invitations) so the
// FE has one consistent shape across both flows. Doctor invites stay on
// their own table+endpoint (license_number / specialty / place_of_work
// are doctor-specific); everything else lands here.
//
// Backend routes (see backend/accounts/urls.py):
//   GET    /api/staff-invitations/                   list (admin)
//   POST   /api/staff-invitations/                   create (admin) → also fires the email
//   DELETE /api/staff-invitations/{id}/              hard-delete (admin)
//   POST   /api/staff-invitations/{id}/revoke/       revoke (admin) — preserves audit
//   POST   /api/staff-invitations/{id}/resend/       re-email same token (admin)
//   GET    /api/staff-invitations/accept/{token}/    public preview
//   POST   /api/staff-invitations/accept/{token}/    public accept → returns { access, refresh, user }
// ============================================================================

import { api } from './client.js'
import { unwrapList } from '../utils/format.js'

// ---- Admin surface ---------------------------------------------------------

export async function listStaffInvitations(params = {}) {
  const { data } = await api.get('/api/staff-invitations/', {
    params: { ordering: '-created_at', page_size: 200, ...params },
  })
  return unwrapList(data)
}

export async function createStaffInvitation(payload) {
  // Backend accepts:
  //   email, first_name, last_name, target_role, is_admin, note,
  //   plus EXACTLY one of:
  //     target_pharmacy / target_laboratory / target_radiology
  //     target_ambulance_service / target_organization
  // The model + serializer + DB CheckConstraint together enforce the
  // "one target matching the role" rule, so a 400 here means the form
  // built the wrong shape.
  const { data } = await api.post('/api/staff-invitations/', payload)
  return data
}

export async function revokeStaffInvitation(id) {
  const { data } = await api.post(`/api/staff-invitations/${id}/revoke/`)
  return data
}

export async function resendStaffInvitation(id) {
  // Re-fires the email using the existing token — does NOT mint a new
  // one, so any link the recipient already has stays valid.
  const { data } = await api.post(`/api/staff-invitations/${id}/resend/`)
  return data
}

// ---- Public preview / accept ----------------------------------------------

/**
 * What the anonymous invitee sees on /accept-invite/staff/<token> BEFORE
 * they type a password. Returns role, target name, expires_at, and the
 * inviter's display name so the recipient can confirm the invite is for them.
 */
export async function previewStaffInvitation(token) {
  const { data } = await api.get(
    `/api/staff-invitations/accept/${encodeURIComponent(token)}/`,
  )
  return data
}

/**
 * Accept the invite — creates the User + role-specific link row + adds
 * the user to the matching auth group. Returns { access, refresh, user }
 * so the FE can sign the new staff member in immediately and route them
 * to their role's home page.
 */
export async function acceptStaffInvitation(token, body) {
  const { data } = await api.post(
    `/api/staff-invitations/accept/${encodeURIComponent(token)}/`,
    body,
  )
  return data
}

// ---- Provider-target lookups for the invite form --------------------------
//
// Each target_role on a StaffInvitation needs a different dropdown of
// candidate targets. Helpers below wrap the existing list endpoints so
// the admin form can call them by role name rather than knowing the
// per-app API path.

import { listLaboratories } from './laboratoryStock.js'
import { listPharmacies } from './pharmacyStock.js'
import { listRadiologyCentres } from './radiologyStock.js'

export async function listAmbulanceServices() {
  const { data } = await api.get('/api/ambulance-services/')
  return unwrapList(data)
}

export async function listB2BOrganizations() {
  // The B2B Organization model (OPIC, Mining Co, etc.) — distinct from
  // the per-provider profile tables. Used only for the org_admin
  // invitation target.
  const { data } = await api.get('/api/organizations/', {
    params: { page_size: 200 },
  })
  return unwrapList(data)
}

/**
 * Map a target_role string onto:
 *   - the FK field name the backend expects on create
 *   - the list-fetcher to populate the target dropdown
 *   - a friendly label for the dropdown's heading
 *
 * Adding a new role means adding one row here. Keeps the form
 * declarative and stops "if role == ..." chains from sprawling.
 */
export const TARGET_ROLE_CONFIG = {
  pharmacy_staff: {
    fkField: 'target_pharmacy',
    targetLabel: 'Pharmacy',
    fetcher: listPharmacies,
  },
  lab_staff: {
    fkField: 'target_laboratory',
    targetLabel: 'Laboratory',
    fetcher: listLaboratories,
  },
  radiology_staff: {
    fkField: 'target_radiology',
    targetLabel: 'Radiology centre',
    fetcher: listRadiologyCentres,
  },
  ambulance_staff: {
    fkField: 'target_ambulance_service',
    targetLabel: 'Ambulance service',
    fetcher: listAmbulanceServices,
  },
  org_admin: {
    fkField: 'target_organization',
    targetLabel: 'Organisation',
    fetcher: listB2BOrganizations,
  },
}

export const TARGET_ROLE_OPTIONS = [
  { value: 'pharmacy_staff', label: 'Pharmacy staff' },
  { value: 'lab_staff', label: 'Laboratory staff' },
  { value: 'radiology_staff', label: 'Radiology staff' },
  { value: 'ambulance_staff', label: 'Ambulance staff' },
  { value: 'org_admin', label: 'Organisation administrator' },
]
